<template>
  <div class="import-question-card mb-1" :class="{ 'import-question-overridden': isOverridden }">
    <div class="d-flex align-items-center flex-wrap">
      <b class="mr-1 price">{{ question.price }}</b>
      <b-badge v-if="specialTypeLabel" variant="light-info" class="mr-50">{{ specialTypeLabel }}</b-badge>
      <b-badge v-if="question.is_for_all" variant="light-info" class="mr-50">для всех</b-badge>

      <b-form-select
        :value="question.mechanic_id"
        :options="mechanicSelectOptions"
        size="sm"
        class="mechanic-select mr-50"
        :disabled="readonly"
        @change="$emit('mechanic', $event)"
      />
      <span v-if="isOverridden" class="text-warning small mr-50">переопределено</span>

      <span class="ml-auto d-flex align-items-center">
        <b-badge
          v-if="unresolvedCount"
          variant="light-danger"
          class="mr-50"
          title="Не найденные в базе сущности при сохранении будут отброшены"
        >{{ unresolvedCount }} не привязано</b-badge>
        <b-badge
          :variant="question.match === 'matched' ? 'light-secondary' : 'light-success'"
          :title="question.match === 'matched' ? `Обновит вопрос #${question.existing_question_id}` : 'Будет создан новый вопрос'"
        >{{ question.match === 'matched' ? 'matched' : 'new' }}</b-badge>
      </span>
    </div>

    <b-row class="mt-1">
      <b-col md="6" class="mb-50">
        <div class="text-muted small text-uppercase mb-25">Вопрос</div>
        <question-parts-preview :parts="question.parts" side="question" />
      </b-col>
      <b-col md="6" class="mb-50">
        <div class="text-muted small text-uppercase mb-25">Ответ</div>
        <b-form-input
          :value="question.answer"
          size="sm"
          class="mb-50"
          placeholder="Текст ответа"
          :state="isAnswerBlank ? false : null"
          :disabled="readonly"
          @input="$emit('field', { field: 'answer', value: $event })"
        />
        <b-form-textarea
          :value="question.answer_comment"
          size="sm"
          rows="1"
          max-rows="4"
          class="mb-50"
          placeholder="Комментарий к ответу"
          :disabled="readonly"
          @input="$emit('field', { field: 'answer_comment', value: $event })"
        />
        <question-parts-preview :parts="question.parts" side="answer" />
      </b-col>
    </b-row>

    <question-bindings-editor
      :slots="slots"
      :value="bindingsValue"
      :unresolved="question.unresolved || []"
      :lookups="lookups"
      :readonly="readonly"
      :context-key="question.source_path"
      @input="$emit('bindings', $event)"
      @resolve="$emit('resolve', $event)"
    />
  </div>
</template>

<script>
import {
  BBadge, BCol, BFormInput, BFormSelect, BFormTextarea, BRow,
} from 'bootstrap-vue'
import { computed } from '@vue/composition-api'
import QuestionPartsPreview from '../components/QuestionPartsPreview.vue'
import QuestionBindingsEditor from '../components/QuestionBindingsEditor.vue'

const SPECIAL_TYPE_LABELS = {
  auction: 'Ставка',
  cat: 'Кот в мешке',
}

export default {
  components: {
    BBadge, BCol, BFormInput, BFormSelect, BFormTextarea, BRow, QuestionPartsPreview, QuestionBindingsEditor,
  },
  props: {
    // Вопрос черновика импорта
    question: { type: Object, required: true },
    // Механика темы — для пометки «переопределено»
    themeMechanicId: { type: Number, default: null },
    // [{id, name}] — доступные механики
    mechanicOptions: { type: Array, default: () => [] },
    // Слоты механики вопроса (или дефолтный слот «Ответ»)
    slots: { type: Array, default: () => [] },
    lookups: { type: Object, default: () => ({}) },
    // Число непривязанных сущностей (чипы без id + не найденные без чипа)
    unresolvedCount: { type: Number, default: 0 },
    readonly: { type: Boolean, default: false },
  },
  setup(props) {
    const mechanicSelectOptions = computed(() => [
      { value: null, text: 'Как у темы' },
      ...props.mechanicOptions.map(mechanic => ({ value: mechanic.id, text: mechanic.name })),
    ])

    const isOverridden = computed(() => props.question.mechanic_id !== null
      && props.question.mechanic_id !== undefined
      && props.question.mechanic_id !== props.themeMechanicId)

    const specialTypeLabel = computed(() => SPECIAL_TYPE_LABELS[props.question.special_type] || '')

    const isAnswerBlank = computed(() => !String(props.question.answer || '').trim())

    const bindingsValue = computed(() => ({
      bindings: props.question.bindings,
      decades: props.question.decades,
      cluster_ids: props.question.cluster_ids,
      origin: props.question.origin,
      slot_values: props.question.slot_values,
    }))

    return {
      mechanicSelectOptions, isOverridden, specialTypeLabel, isAnswerBlank, bindingsValue,
    }
  },
}
</script>

<style scoped>
.import-question-card {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  padding: 0.75rem;
}
.import-question-overridden {
  border-color: #ff9f43;
}
.price {
  min-width: 2.5rem;
}
.mechanic-select {
  max-width: 260px;
}
</style>
